import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Table, Tag, Space, message, Typography, Row, Col, Statistic,
  Popconfirm, Empty, Tooltip,
} from 'antd';
import {
  PlusOutlined, EyeOutlined, DeleteOutlined, ExportOutlined,
  FileTextOutlined, SyncOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { KSGService } from '../api/services/ksg.service';

const { Title, Text } = Typography;

const statusOptions = [
  { value: 'draft', label: 'Черновик', color: 'default' },
  { value: 'ready', label: 'Готов', color: 'blue' },
  { value: 'exported', label: 'Экспортирован', color: 'green' },
];

export default function KSGDraftsPage() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchDrafts = () => {
    setLoading(true);
    KSGService.getDrafts()
      .then(r => setDrafts(r.data))
      .catch(() => message.error('Ошибка загрузки'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDrafts(); }, []);

  const handleDelete = async (id) => {
    try {
      await KSGService.deleteDraft(id);
      message.success('Черновик удалён');
      fetchDrafts();
    } catch { message.error('Ошибка удаления'); }
  };

  const columns = [
    { title: 'Название', dataIndex: 'name', key: 'name',
      render: (name, r) => (
        <Button type="link" onClick={() => navigate(`/ksg/drafts/${r.id}`)} style={{ fontWeight: 600, padding: 0 }}>
          {name}
        </Button>
      ),
    },
    { title: 'Статус', dataIndex: 'status', key: 'status', width: 140,
      render: (s) => {
        const opt = statusOptions.find(o => o.value === s);
        return opt ? <Tag color={opt.color}>{opt.label}</Tag> : <Tag>{s}</Tag>;
      },
    },
    { title: 'Задач', dataIndex: 'tasks_count', key: 'tasks_count', width: 100, align: 'center' },
    { title: 'Длительность', dataIndex: 'total_duration_days', key: 'total_duration_days', width: 120,
      render: (v) => v ? `${v} дней` : '—',
    },
    { title: 'Стоимость', dataIndex: 'total_cost', key: 'total_cost', width: 150, align: 'right',
      render: (v) => v ? `${v.toLocaleString()} ₽` : '—',
    },
    { title: 'Дата начала', dataIndex: 'project_start_date', key: 'project_start_date', width: 120,
      render: (d) => d ? dayjs(d).format('DD.MM.YYYY') : '—',
    },
    { title: 'Обновлён', dataIndex: 'updated_at', key: 'updated_at', width: 120,
      render: (d) => dayjs(d).format('DD.MM.YYYY HH:mm'),
    },
    { title: 'Действия', key: 'actions', width: 150,
      render: (_, r) => (
        <Space>
          <Tooltip title="Открыть">
            <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/ksg/drafts/${r.id}`)} />
          </Tooltip>
          {r.status === 'exported' ? (
            <Tooltip title="Перейти к проекту">
              <Button size="small" icon={<ExportOutlined />} onClick={() => navigate('/tasks')} />
            </Tooltip>
          ) : (
            <Tooltip title="Экспортировать">
              <Button size="small" icon={<ExportOutlined />} onClick={() => navigate(`/ksg/drafts/${r.id}`)} />
            </Tooltip>
          )}
          <Popconfirm title="Удалить черновик?" onConfirm={() => handleDelete(r.id)} okText="Удалить" cancelText="Отмена">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">Черновики КСГ</h1>
          <p className="page-subtitle">Управление сгенерированными комплексными стандартными группами</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/ksg/wizard')}
          style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', border: 'none' }}
        >
          Создать КСГ
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card size="small"><Statistic title="Всего черновиков" value={drafts.length} prefix={<FileTextOutlined />} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="Черновики" value={drafts.filter(d => d.status === 'draft').length} valueStyle={{ color: '#8c8c8c' }} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="Готовы" value={drafts.filter(d => d.status === 'ready').length} valueStyle={{ color: '#1677ff' }} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="Экспортированы" value={drafts.filter(d => d.status === 'exported').length} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
      </Row>

      <Card style={{ borderRadius: 16 }} styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={drafts}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          locale={{ emptyText: <Empty description="Нет черновиков. Создайте первую КСГ!" /> }}
        />
      </Card>
    </div>
  );
}
